// helper to transform metadata into InfoTable rows
// if schema is provided, it attempts to map types. otherwise defaults to string/guessed type.
export const transformDataToRows = (data, schema = {}) => {
    const newRows = [];
    const processedSchemaKeys = new Set();

    // add existing data fields, applying schema info if available
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const fieldSchema = schema[key];
        let info = {
          value: data[key],
          type: 'string',
          choices: [],
        };

        // if the value is an empty object treat it as undefined
        if (typeof info.value === 'object' && info.value !== null && Object.keys(info.value).length === 0) {
          info.value = undefined;
        }

        // if the value is undefined or null, default it to an empty array
        if (info.value === undefined || info.value === null) {
          info.value = [];
        }

        if (fieldSchema) {
          info.type = fieldSchema.field_type || 'string';
          info.choices = fieldSchema.choices || [];
          info.isCustom = false;
          processedSchemaKeys.add(key);
        } else {
          if (typeof data[key] === 'boolean') {
            info.type = 'bool';
          } else if (typeof data[key] === 'number') {
            info.type = 'int';
          } else if (Array.isArray(data[key])) {
            info.type = 'multi';
          }
        }

        newRows.push({
          label: fieldSchema?.title || key,
          key: key,
          information: info,
        });
      }
    }

    for (const key in schema) {
      if (schema.hasOwnProperty(key) && !processedSchemaKeys.has(key)) {
        const fieldSchema = schema[key];
        
        let defaultValue = fieldSchema.default_value;

        // check if default_value is an empty object
        if (typeof defaultValue === 'object' && defaultValue !== null && Object.keys(defaultValue).length === 0) {
          defaultValue = undefined;
        }

        // ensures default value for choice/multi types is an empty array if it's currently undefined/null
        if ((fieldSchema.field_type === 'choice' || fieldSchema.field_type === 'multi') && 
            (defaultValue === undefined || defaultValue === null)) {
          defaultValue = []; 
        }

        newRows.push({
          label: fieldSchema?.title || key,
          key: key,
          information: {
            value: defaultValue, 
            type: fieldSchema.field_type,
            choices: fieldSchema.choices || [],
            isCustom: false
          },
        });
      }
    }

    return newRows;
};


// common styles for outlined inputs (TextField, Select)
export const commonOutlinedInputStyles = {
  width: '100%',
  fontFamily: 'inherit',
  // border and focus states
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#54c1a5',
    },
    '&:hover fieldset': {
      borderColor: '#54c1a5',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#54c1a5',
      borderWidth: '2px',
    },
  },
  // notched outline for Select
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#54c1a5',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#54c1a5',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#54c1a5',
    borderWidth: '2px',
  },
  // dropdown icon
  '& .MuiSelect-iconOutlined': {
    color: '#54c1a5',
  },
};

// common styles for MenuItems (for Select components)
export const commonMenuItemStyles = {
  fontFamily: 'inherit',
  '&:hover': {
    backgroundColor: 'rgba(84, 193, 165, 0.1)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(84, 193, 165, 0.2)',
    color: 'inherit',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(84, 193, 165, 0.25)',
  },
};

// common styles for Checkbox
export const commonCheckboxStyles = {
  color: '#54c1a5',
  '&.Mui-checked': {
    color: '#54c1a5',
  },
  '&.Mui-checked:hover': {
    backgroundColor: 'rgba(84, 193, 165, 0.04)',
  },
  '&:hover': {
    backgroundColor: 'rgba(84, 193, 165, 0.04)',
  },
};

// common styles for Chips within Multi-select
export const commonChipStyles = {
  fontFamily: 'inherit',
  borderColor: '#54c1a5',
  color: '#54c1a5',
  '&:hover': {
    backgroundColor: 'rgba(84, 193, 165, 0.04)',
  },
};

// common styles for Chips displaying selected items (filled background)
export const selectedChipStyles = {
  backgroundColor: '#54c1a5',
  color: '#fff',
};